import React, { useState } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { openModal } from 'reducers/modals';
// store
import { postComment, deleteUnsavedComment, pushUnsavedComment } from 'reducers/siteVisitReport';
// components
import MentionsInput from 'components/MentionsInput';
import Button from 'components/Button';
import SpeechToText from 'components/SpeechToText';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import { FormattedMessage, useIntl } from 'react-intl';
// hooks
import useCommentWithMentionsState from 'hooks/useCommentWithMentionsState';
// router
import { withRouter } from 'react-router';
// utils
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import classnames from 'classnames/bind';
import mixpanel from 'mixpanel-browser';
// styles
import styles from './QuestionNewComment.module.scss';

const cn = classnames.bind(styles);

const svrQuestionMentionRoles = ['owner', 'caretaker', 'manager', 'vet'];

const QuestionNewComment = ({
  setAnswer,
  question,
  params,
  postComment,
  pushUnsavedComment,
  deleteUnsavedComment,
  openModal,
  currentUser
}) => {
  const { comment, mentions, resetCommentWithMentions, setComment, setMentions } = useCommentWithMentionsState();
  const [commentInterim, setCommentInterim] = useState('');
  const { formatMessage } = useIntl();

  const handleSetMentions = (mentions) => {
    const commentData = { comment_mentions_attributes: mentions.map(({ id: user_id }) => ({ user_id })) };
    pushUnsavedComment(commentData);
    setMentions(mentions);
  };

  const handleSetComment = (comment) => {
    const commentData = { comment };
    pushUnsavedComment(commentData);
    setComment(comment);
  };

  const handleSaveComment = async () => {
    if (currentUser && currentUser.id) {
      mixpanel.track('Feature Used', {
        user_id: currentUser.id,
        feature_name: 'QuestionNewComment',
        action: 'Save Comment',
        question_label: question.label,
        farm_id: params.farmId,
        has_mentions: mentions.length > 0,
        timestamp: new Date().toISOString()
      });
    }

    try {
      const fullComment = `${comment || ''}${commentInterim || ''}`;
      const commentData = {
        comment: fullComment, comment_mentions_attributes: mentions.map(({ id: user_id }) => ({ user_id }))
      };
      await postComment(commentData);
      deleteUnsavedComment();
      resetCommentWithMentions();
      setCommentInterim('');
    } catch (error) {
      toastResponseErrors(error);
    }
  };

  const toggleFlag = (newFlagState) => {
    setAnswer({ is_flagged: newFlagState });
  };

  const handleToggleFlag = () => {
    const newFlagState = !question.answer?.is_flagged;

    if (currentUser && currentUser.id) {
      mixpanel.track('Feature Used', {
        user_id: currentUser.id,
        feature_name: 'QuestionNewComment',
        action: newFlagState ? 'Flag For Followup' : 'Unflag For Followup',
        question_label: question.label,
        farm_id: params.farmId,
        timestamp: new Date().toISOString()
      });
    }

    if (question.answer?.is_corrected && newFlagState) {
      openModal(
        <ConfirmationModal
          title={<FormattedMessage id="general.removeCorrectedStatus" />}
          actionBtnLabel={<FormattedMessage id="component.modal.allGood.confirm" />}
          actionBtnProps={{ primary: true }}
          handleConfirm={() => toggleFlag(newFlagState)}
        >
          <FormattedMessage id="general.removeCorrectedStatusDesc" />
        </ConfirmationModal>
      );
      return;
    }

    toggleFlag(newFlagState);
  };

  return (
    <div className={cn('note-section')}>
      <div className={cn('mentions-input-block')}>
        <MentionsInput
          comment={`${comment || ''}${commentInterim || ''}`}
          disabledIds={mentions.map((mention) => mention.id)}
          entityType="farm"
          entityId={params.farmId}
          inputType="square"
          mentionRoles={svrQuestionMentionRoles}
          onCommentChange={handleSetComment}
          onMentionsChange={handleSetMentions}
          placeholder={formatMessage({ id: 'general.tellUsMore' })}
        />
      </div>

      <div className={cn('note-actions')}>
        <div className={cn('speech-to-text-container')}>
          <SpeechToText
            setTextMain={setComment}
            setTextInterim={setCommentInterim}
            className="speech-to-text"
            buttonClassName="control-button cb-medium main-control primary"
            iconClassName="speech-icon"
          />
        </div>
        {question?.answer?.value && (
          <div
            onClick={handleToggleFlag}
            className={cn('flag-button', {
              flagged: question.answer?.is_flagged,
            })}
          >
            <i className={cn('fa fa-flag')} />
            <span className={cn('flag-btn-label')}>
              {question.answer?.is_flagged ? (
                <FormattedMessage id="general.unflagForFollowup" />
              ) : (
                <FormattedMessage id="general.flagForFollowup" />
              )}
            </span>
          </div>
        )}

        <Button
          onClick={handleSaveComment}
          disabled={!comment && !commentInterim}
          className={cn('save-btn')}
          primary
        >
          <FormattedMessage id="general.button.save" />
        </Button>
      </div>
    </div>
  );
};

QuestionNewComment.propTypes = {
  setAnswer: T.func.isRequired,
  question: T.object.isRequired,
  params: T.object.isRequired,
  postComment: T.func.isRequired,
  pushUnsavedComment: T.func.isRequired,
  deleteUnsavedComment: T.func.isRequired,
  openModal: T.func.isRequired,
  currentUser: T.object,
};

export default withRouter(
  connect((state) => ({
    currentUser: state.auth.user,
  }), (dispatch, { questionIndex, categoryIndex }) => ({
    postComment: (commentData) => dispatch(postComment(categoryIndex, questionIndex, commentData)),
    deleteUnsavedComment: () => dispatch(deleteUnsavedComment(`${categoryIndex}-${questionIndex}`)),
    pushUnsavedComment: (comment) => dispatch(pushUnsavedComment({
      ...comment,
      questionIndex,
      categoryIndex,
      key: `${categoryIndex}-${questionIndex}`
    })),
    openModal: (component) => dispatch(openModal(component)),
  }))(QuestionNewComment)
);
